import { z } from 'zod'
import { resultVisibilitySchema } from './competition-settings'
import { publicIdSchema, utcTimestampSchema } from './primitives'

export const scoreItemInputSchema = z.strictObject({
  rubricItemId: publicIdSchema,
  rawScore: z.number().finite(),
  comment: z.string().max(4000).nullable().default(null),
})
export const saveScoreRequestSchema = z.strictObject({
  // A draft may hold only the criteria scored so far (or none yet); the route
  // requires every criterion again on final submit.
  items: z.array(scoreItemInputSchema),
  comment: z.string().max(8000).nullable().default(null),
  // UX-134/API-078: organizer-visible, distinct from `comment` above (which
  // stays judge-private — see UX-117/UX-124's "僅您本人可見" declaration).
  reviewComment: z.string().max(8000).nullable().default(null),
})
export type SaveScoreRequest = z.infer<typeof saveScoreRequestSchema>
export const reopenScoreRequestSchema = z.strictObject({
  reason: z.string().trim().min(1).max(2000),
})
export const calculateResultsRequestSchema = z.strictObject({
  roundId: publicIdSchema,
  categoryId: publicIdSchema.nullable().default(null),
})
export type CalculateResultsRequest = z.infer<typeof calculateResultsRequestSchema>
export const AWARD_TIERS = ['first', 'second', 'third', 'honorable', 'vacant'] as const
export const awardTierSchema = z.enum(AWARD_TIERS)
export type AwardTier = z.infer<typeof awardTierSchema>
export const overrideResultRequestSchema = z.strictObject({
  submissionId: publicIdSchema,
  awardTierOverride: awardTierSchema.nullable(),
  reason: z.string().trim().min(1).max(2000),
})
export type OverrideResultRequest = z.infer<typeof overrideResultRequestSchema>
export const scoreDtoSchema = z.strictObject({
  id: publicIdSchema,
  assignmentId: publicIdSchema,
  submissionId: publicIdSchema,
  status: z.enum(['draft', 'submitted', 'locked', 'reopened']),
  totalScore: z.number().nullable(),
  comment: z.string().nullable(),
  reviewComment: z.string().nullable(),
  items: z.array(scoreItemInputSchema.extend({ weightedScore: z.number() })),
  submittedAt: utcTimestampSchema.nullable(),
  updatedAt: utcTimestampSchema,
})
export type ScoreDto = z.infer<typeof scoreDtoSchema>
/**
 * UX-134/API-078: the organizer-facing shape of a score — deliberately
 * distinct from `ScoreDto`, not a `Pick`/`Omit` of it, so a future field
 * added to `ScoreDto` doesn't silently leak to organizers by inheritance.
 * Excludes the judge-private `comment` (both score-level and per-item)
 * entirely; `reviewComment` is the only commentary field organizers see.
 */
export const adminScoreSummaryDtoSchema = z.strictObject({
  /** Where this score came from; judge scores are always `judge`. */
  source: z.literal('judge'),
  id: publicIdSchema,
  assignmentId: publicIdSchema,
  submissionId: publicIdSchema,
  judgeUserId: publicIdSchema,
  judgeDisplayName: z.string(),
  status: z.enum(['draft', 'submitted', 'locked', 'reopened']),
  totalScore: z.number().nullable(),
  reviewComment: z.string().nullable(),
  submittedAt: utcTimestampSchema.nullable(),
  updatedAt: utcTimestampSchema,
})
export type AdminScoreSummaryDto = z.infer<typeof adminScoreSummaryDtoSchema>
/**
 * PR-320: one finished score's review comment, for the organizer's bulk read.
 * Its own shape (never a pick of `ScoreDto`) so the judge-private note cannot
 * leak through inheritance.
 */
export const adminReviewCommentDtoSchema = z.strictObject({
  scoreId: publicIdSchema,
  submissionId: publicIdSchema,
  judgeDisplayName: z.string(),
  status: z.enum(['submitted', 'locked']),
  reviewComment: z.string().nullable(),
})
export type AdminReviewCommentDto = z.infer<typeof adminReviewCommentDtoSchema>
export const resultRunDtoSchema = z.strictObject({
  id: publicIdSchema,
  roundId: publicIdSchema,
  categoryId: publicIdSchema.nullable(),
  status: z.enum(['draft', 'calculated', 'locked', 'published']),
  calculationMethod: z.enum(['average', 'weighted_average']),
  results: z.array(
    z.strictObject({
      submissionId: publicIdSchema,
      finalScore: z.number(),
      rank: z.int().positive(),
      awardTierOverride: awardTierSchema.nullable(),
      overrideReason: z.string().nullable(),
      /**
       * Present only for a round that takes staff input (a judge_only round
       * keeps its original shape): the judges' average (null for staff_only).
       */
      judgeScore: z.number().nullable().optional(),
      /** Present only for a round that takes staff input: net of add and deduct values. */
      manualAdjustment: z.number().nullable().optional(),
    }),
  ),
  excludedSubmissionIds: z.array(publicIdSchema),
  createdAt: utcTimestampSchema,
  /** SDD v1.2 §17.2 Result Run Card metadata ("Calculated By"). Always present — `calculate` always has an actor. */
  calculatedByUserId: publicIdSchema,
  calculatedByDisplayName: z.string(),
  lockedAt: utcTimestampSchema.nullable(),
  /** `null` until locked, and for any run locked before this field existed (migrations/0036). */
  lockedByUserId: publicIdSchema.nullable(),
  lockedByDisplayName: z.string().nullable(),
  publishedAt: utcTimestampSchema.nullable(),
  /** `null` until published, and for any run published before this field existed (migrations/0036). */
  publishedByUserId: publicIdSchema.nullable(),
  publishedByDisplayName: z.string().nullable(),
})
export type ResultRunDto = z.infer<typeof resultRunDtoSchema>

/**
 * API-109/AC-279: the student-facing, own-team-only view of a published
 * result. Deliberately its own shape, not a `Pick`/derivative of
 * `ResultRunDto` — that type's `results` array holds every team in the run,
 * and this one must never be able to inherit that by accident as fields get
 * added later (same reasoning as `AdminScoreSummaryDto` staying separate
 * from `ScoreDto`, see SEC-048). Carries no `result_runs.id`,
 * `calculationMethod`, or snapshot data beyond what the caller's own team is
 * allowed to see.
 */
export const studentTeamResultEntryDtoSchema = z.strictObject({
  roundId: publicIdSchema,
  roundName: z.string(),
  categoryId: publicIdSchema.nullable(),
  categoryName: z.string().nullable(),
  publishedAt: utcTimestampSchema,
  included: z.boolean(),
  finalScore: z.number().nullable(),
  rank: z.int().positive().nullable(),
  awardTier: awardTierSchema.nullable(),
  /**
   * Own-team breakdown, present only for a round that took staff input:
   * the judges' average (an aggregate, never one judge's score), the net
   * manual adjustment, and the add/deduct lines behind it.
   */
  judgeScore: z.number().nullable().optional(),
  manualAdjustment: z.number().nullable().optional(),
  manualItems: z
    .array(
      z.strictObject({
        name: z.string(),
        scoringEffect: z.enum(['add', 'deduct']),
        value: z.number(),
      }),
    )
    .optional(),
})
export type StudentTeamResultEntryDto = z.infer<typeof studentTeamResultEntryDtoSchema>

export const studentTeamResultsDtoSchema = z.strictObject({
  resultVisibility: resultVisibilitySchema,
  entries: z.array(studentTeamResultEntryDtoSchema),
})
export type StudentTeamResultsDto = z.infer<typeof studentTeamResultsDtoSchema>

/**
 * Organizer-only comparison of FINISHED scores (locked or submitted) per
 * criterion across judges. Criteria are matched by name so different versions
 * of one rubric line up. Judge-private notes and drafts are never included.
 */
export const scoreMatrixDtoSchema = z.strictObject({
  criteria: z.array(
    z.strictObject({
      key: z.string(),
      name: z.string(),
      weight: z.number(),
      maxScore: z.number().nullable(),
    }),
  ),
  judges: z.array(
    z.strictObject({
      scoreId: publicIdSchema,
      judgeUserId: publicIdSchema,
      displayName: z.string(),
      status: z.enum(['draft', 'submitted', 'locked', 'reopened']),
      totalScore: z.number().nullable(),
      values: z.record(z.string(), z.number().nullable()),
    }),
  ),
})
export type ScoreMatrixDto = z.infer<typeof scoreMatrixDtoSchema>
